import React, { useState, useMemo } from "react";
import {
	CalendarDays,
	ChevronLeft,
	ChevronRight,
	Plus,
	X,
	Trash2,
	Pencil,
	Dumbbell,
	TrendingUp,
	ChevronDown,
	ChevronUp,
	MessageSquare,
} from "lucide-react";
import CustomModal from "./CustomModal";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const PLACEHOLDER_MAP = {
	time: "5:30",
	weight: "100",
	reps: "20",
	rounds: "10",
	distance: "5.2",
	calories: "350",
};

const TrainerPlanner = ({
	plannerEntries = [],
	userRecords = [],
	movements = [],
	countTypes = [],
	onAddEntry,
	onUpdateEntry,
	onDeleteEntry,
	onAddRecord,
	onDeleteRecord,
	loading = false,
}) => {
	const today = new Date();
	const [currentMonth, setCurrentMonth] = useState(today.getMonth());
	const [currentYear, setCurrentYear] = useState(today.getFullYear());
	const [selectedDate, setSelectedDate] = useState(null);
	const [showForm, setShowForm] = useState(false);
	const [editingEntry, setEditingEntry] = useState(null);
	const [formData, setFormData] = useState({
		title: "",
		description: "",
		comments: "",
		type: "workout",
	});
	// Movement record form state — keyed by planner entry id
	const [showRecordForm, setShowRecordForm] = useState({});
	const [recordFormData, setRecordFormData] = useState({});
	const [expandedRecords, setExpandedRecords] = useState({});
	const [editingComment, setEditingComment] = useState({}); // { [entryId]: true }
	const [commentDrafts, setCommentDrafts] = useState({}); // { [entryId]: "text" }

	const [modal, setModal] = useState({
		show: false,
		title: "",
		message: "",
		type: "info",
	});
	const [confirmModal, setConfirmModal] = useState({
		show: false,
		title: "",
		message: "",
		type: "delete",
		onConfirm: null,
	});

	// Build calendar grid
	const calendarDays = useMemo(() => {
		const firstDay = new Date(currentYear, currentMonth, 1);
		const lastDay = new Date(currentYear, currentMonth + 1, 0);
		let startDay = firstDay.getDay() - 1;
		if (startDay < 0) startDay = 6;

		const days = [];
		for (let i = 0; i < startDay; i++) days.push(null);
		for (let d = 1; d <= lastDay.getDate(); d++) days.push(d);
		return days;
	}, [currentMonth, currentYear]);

	// Index entries by date
	const entriesByDate = useMemo(() => {
		const map = {};
		plannerEntries.forEach((entry) => {
			if (!map[entry.date]) map[entry.date] = [];
			map[entry.date].push(entry);
		});
		return map;
	}, [plannerEntries]);

	// Index records by plannerEntryId
	const recordsByEntry = useMemo(() => {
		const map = {};
		userRecords.forEach((rec) => {
			if (rec.plannerEntryId) {
				if (!map[rec.plannerEntryId]) map[rec.plannerEntryId] = [];
				map[rec.plannerEntryId].push(rec);
			}
		});
		return map;
	}, [userRecords]);

	const getDateKey = (day) => {
		const m = String(currentMonth + 1).padStart(2, "0");
		const d = String(day).padStart(2, "0");
		return `${currentYear}-${m}-${d}`;
	};

	const isToday = (day) =>
		day === today.getDate() &&
		currentMonth === today.getMonth() &&
		currentYear === today.getFullYear();

	const prevMonth = () => {
		if (currentMonth === 0) {
			setCurrentMonth(11);
			setCurrentYear(currentYear - 1);
		} else setCurrentMonth(currentMonth - 1);
	};

	const nextMonth = () => {
		if (currentMonth === 11) {
			setCurrentMonth(0);
			setCurrentYear(currentYear + 1);
		} else setCurrentMonth(currentMonth + 1);
	};

	const goToToday = () => {
		setCurrentMonth(today.getMonth());
		setCurrentYear(today.getFullYear());
		setSelectedDate(today.toISOString().split("T")[0]);
	};

	const handleDayClick = (day) => {
		if (!day) return;
		setSelectedDate(getDateKey(day));
		setShowForm(false);
		setEditingEntry(null);
	};

	const openAddForm = () => {
		setFormData({ title: "", description: "", comments: "", type: "workout" });
		setEditingEntry(null);
		setShowForm(true);
	};

	const openEditForm = (entry) => {
		setFormData({
			title: entry.title,
			description: entry.description || "",
			comments: entry.comments || "",
			type: entry.type || "workout",
		});
		setEditingEntry(entry);
		setShowForm(true);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!formData.title.trim()) {
			setModal({
				show: true,
				title: "Missing Title",
				message: "Please enter a workout title.",
				type: "warning",
			});
			return;
		}
		try {
			if (editingEntry) {
				await onUpdateEntry(editingEntry.id, {
					title: formData.title.trim(),
					description: formData.description.trim(),
					comments: formData.comments.trim(),
					type: formData.type,
					date: selectedDate,
				});
			} else {
				await onAddEntry({
					title: formData.title.trim(),
					description: formData.description.trim(),
					comments: formData.comments.trim(),
					type: formData.type,
					date: selectedDate,
				});
			}
			setShowForm(false);
			setEditingEntry(null);
			setFormData({
				title: "",
				description: "",
				comments: "",
				type: "workout",
			});
		} catch (error) {
			setModal({
				show: true,
				title: "Error",
				message: error.message || "Failed to save entry.",
				type: "error",
			});
		}
	};

	const handleDelete = (entry) => {
		setConfirmModal({
			show: true,
			title: "Delete Entry",
			message: `Delete "${entry.title}" from your planner?`,
			type: "delete",
			onConfirm: async () => {
				setConfirmModal((prev) => ({ ...prev, show: false }));
				try {
					await onDeleteEntry(entry.id);
				} catch (error) {
					setModal({
						show: true,
						title: "Error",
						message: error.message || "Failed to delete entry.",
						type: "error",
					});
				}
			},
		});
	};

	// --- Movement record helpers ---
	const toggleRecordForm = (entryId) => {
		setShowRecordForm((prev) => ({ ...prev, [entryId]: !prev[entryId] }));
		if (!recordFormData[entryId]) {
			setRecordFormData((prev) => ({
				...prev,
				[entryId]: {
					movement: "",
					measurements: [
						{ countType: "weight", value: "" },
						{ countType: "reps", value: "" },
					],
				},
			}));
		}
	};

	const updateRecordField = (entryId, field, value) => {
		setRecordFormData((prev) => ({
			...prev,
			[entryId]: { ...prev[entryId], [field]: value },
		}));
	};

	const updateRecordMeasurement = (entryId, idx, field, val) => {
		setRecordFormData((prev) => {
			const form = { ...prev[entryId] };
			form.measurements = form.measurements.map((m, i) =>
				i === idx ? { ...m, [field]: val } : m,
			);
			return { ...prev, [entryId]: form };
		});
	};

	const addRecordMeasurement = (entryId) => {
		setRecordFormData((prev) => {
			const form = { ...prev[entryId] };
			form.measurements = [...form.measurements, { countType: "", value: "" }];
			return { ...prev, [entryId]: form };
		});
	};

	const removeRecordMeasurement = (entryId, idx) => {
		setRecordFormData((prev) => {
			const form = { ...prev[entryId] };
			form.measurements = form.measurements.filter((_, i) => i !== idx);
			return { ...prev, [entryId]: form };
		});
	};

	const handleAddRecord = async (entryId) => {
		const form = recordFormData[entryId];
		if (!form?.movement) {
			setModal({
				show: true,
				title: "Missing Movement",
				message: "Please select a movement.",
				type: "warning",
			});
			return;
		}
		const validMeasurements = form.measurements.filter(
			(m) => m.countType && m.value,
		);
		if (validMeasurements.length === 0) {
			setModal({
				show: true,
				title: "Missing Measurement",
				message: "Please add at least one measurement.",
				type: "warning",
			});
			return;
		}

		const measurements = validMeasurements.map((m) => {
			const info = countTypes.find((ct) => ct.value === m.countType);
			return {
				countType: m.countType,
				countTypeLabel: info?.label || m.countType,
				countUnit: info?.unit || "",
				value: m.value.trim(),
			};
		});
		const primary = measurements[0];

		try {
			await onAddRecord({
				movement: form.movement,
				countType: primary.countType,
				countTypeLabel: primary.countTypeLabel,
				countUnit: primary.countUnit,
				value: primary.value,
				measurements,
				notes: "",
				date: selectedDate,
				plannerEntryId: entryId,
			});
			// Reset form
			setRecordFormData((prev) => ({
				...prev,
				[entryId]: {
					movement: "",
					measurements: [
						{ countType: "weight", value: "" },
						{ countType: "reps", value: "" },
					],
				},
			}));
			setShowRecordForm((prev) => ({ ...prev, [entryId]: false }));
		} catch (error) {
			setModal({
				show: true,
				title: "Error",
				message: error.message || "Failed to save movement.",
				type: "error",
			});
		}
	};

	const handleDeleteRecord = (recordId) => {
		setConfirmModal({
			show: true,
			title: "Delete Movement",
			message: "Delete this movement record?",
			type: "delete",
			onConfirm: async () => {
				setConfirmModal((prev) => ({ ...prev, show: false }));
				try {
					await onDeleteRecord(recordId);
				} catch (error) {
					setModal({
						show: true,
						title: "Error",
						message: error.message || "Failed to delete.",
						type: "error",
					});
				}
			},
		});
	};

	const selectedEntries = selectedDate ? entriesByDate[selectedDate] || [] : [];
	const monthName = new Date(currentYear, currentMonth).toLocaleString(
		"default",
		{ month: "long", year: "numeric" },
	);

	const typeColors = {
		workout: "#d15a36",
		cardio: "#28a745",
		rest: "#6c757d",
		mobility: "#17a2b8",
	};

	const darkInput = {
		backgroundColor: "#1a1a1a",
		color: "#fff",
		border: "1px solid #555",
	};

	return (
		<div className="card">
			<div className="card-header d-flex justify-content-between align-items-center">
				<h5 className="mb-0">
					<CalendarDays className="me-2" size={20} />
					Workout Planner
				</h5>
				<button
					className="btn btn-outline-secondary btn-sm"
					onClick={goToToday}
				>
					Today
				</button>
			</div>
			<div className="card-body">
				{/* Month Navigation */}
				<div className="d-flex justify-content-between align-items-center mb-3">
					<button
						className="btn btn-sm btn-outline-secondary"
						onClick={prevMonth}
					>
						<ChevronLeft size={18} />
					</button>
					<h6 className="mb-0">{monthName}</h6>
					<button
						className="btn btn-sm btn-outline-secondary"
						onClick={nextMonth}
					>
						<ChevronRight size={18} />
					</button>
				</div>

				{/* Calendar Grid */}
				<div
					style={{
						display: "grid",
						gridTemplateColumns: "repeat(7, 1fr)",
						gap: "2px",
					}}
				>
					{DAYS.map((day) => (
						<div
							key={day}
							className="text-center py-1"
							style={{
								fontSize: "0.75rem",
								fontWeight: 600,
								color: "rgba(255,255,255,0.5)",
							}}
						>
							{day}
						</div>
					))}
					{calendarDays.map((day, idx) => {
						if (!day) return <div key={`empty-${idx}`} />;
						const dateKey = getDateKey(day);
						const dayEntries = entriesByDate[dateKey] || [];
						const isSelected = selectedDate === dateKey;
						const isTodayCell = isToday(day);
						return (
							<div
								key={dateKey}
								onClick={() => handleDayClick(day)}
								style={{
									minHeight: "48px",
									padding: "4px",
									borderRadius: "6px",
									cursor: "pointer",
									backgroundColor: isSelected
										? "rgba(209,90,54,0.2)"
										: isTodayCell
											? "rgba(209,90,54,0.08)"
											: "transparent",
									border: isSelected
										? "2px solid #d15a36"
										: isTodayCell
											? "1px solid rgba(209,90,54,0.4)"
											: "1px solid rgba(255,255,255,0.08)",
									transition: "all 0.15s ease",
								}}
							>
								<div
									className="text-center"
									style={{
										fontSize: "0.8rem",
										fontWeight: isTodayCell ? 700 : 400,
										color: isTodayCell ? "#d15a36" : "#fff",
									}}
								>
									{day}
								</div>
								{dayEntries.length > 0 && (
									<div className="d-flex justify-content-center gap-1 mt-1 flex-wrap">
										{dayEntries.slice(0, 3).map((entry, i) => (
											<div
												key={i}
												style={{
													width: "6px",
													height: "6px",
													borderRadius: "50%",
													backgroundColor: typeColors[entry.type] || "#d15a36",
												}}
											/>
										))}
									</div>
								)}
							</div>
						);
					})}
				</div>

				{/* Selected Day Detail */}
				{selectedDate && (
					<div className="mt-4">
						<div className="d-flex justify-content-between align-items-center mb-3">
							<h6 className="mb-0">
								{new Date(selectedDate + "T00:00:00").toLocaleDateString(
									"en-GB",
									{
										weekday: "long",
										day: "numeric",
										month: "long",
										year: "numeric",
									},
								)}
							</h6>
							<button
								className="btn btn-sm btn-primary"
								onClick={openAddForm}
								disabled={loading}
							>
								<Plus size={16} className="me-1" />
								<span className="d-none d-sm-inline">Add Workout</span>
							</button>
						</div>

						{/* Add/Edit Form */}
						{showForm && (
							<div
								className="mb-3 p-3 rounded"
								style={{ backgroundColor: "#2a2a2a", border: "1px solid #444" }}
							>
								<div className="d-flex justify-content-between align-items-center mb-2">
									<span style={{ color: "#fff", fontWeight: 600 }}>
										{editingEntry ? "Edit Entry" : "New Entry"}
									</span>
									<button
										className="btn btn-sm p-0"
										onClick={() => {
											setShowForm(false);
											setEditingEntry(null);
										}}
										style={{ color: "rgba(255,255,255,0.5)" }}
									>
										<X size={16} />
									</button>
								</div>
								<form onSubmit={handleSubmit}>
									<div className="row g-2">
										<div className="col-12 col-sm-8">
											<input
												type="text"
												className="form-control form-control-sm"
												placeholder="Workout title"
												value={formData.title}
												onChange={(e) =>
													setFormData({ ...formData, title: e.target.value })
												}
												disabled={loading}
												style={darkInput}
											/>
										</div>
										<div className="col-12 col-sm-4">
											<select
												className="form-select form-select-sm"
												value={formData.type}
												onChange={(e) =>
													setFormData({ ...formData, type: e.target.value })
												}
												disabled={loading}
												style={darkInput}
											>
												<option value="workout">Workout</option>
												<option value="cardio">Cardio</option>
												<option value="mobility">Mobility</option>
												<option value="rest">Rest Day</option>
											</select>
										</div>
										<div className="col-12">
											<textarea
												className="form-control"
												rows="6"
												placeholder="Paste or type your workout details here..."
												value={formData.description}
												onChange={(e) =>
													setFormData({
														...formData,
														description: e.target.value,
													})
												}
												disabled={loading}
												style={{
													...darkInput,
													fontSize: "0.85rem",
													lineHeight: "1.5",
												}}
											/>
										</div>
										<div className="col-12">
											<div className="d-flex align-items-center gap-1 mb-1">
												<MessageSquare
													size={14}
													style={{ color: "rgba(255,255,255,0.5)" }}
												/>
												<small style={{ color: "rgba(255,255,255,0.5)" }}>
													Comments
												</small>
											</div>
											<textarea
												className="form-control form-control-sm"
												rows="3"
												placeholder="Add your notes, thoughts, or modifications..."
												value={formData.comments}
												onChange={(e) =>
													setFormData({
														...formData,
														comments: e.target.value,
													})
												}
												disabled={loading}
												style={{
													...darkInput,
													fontSize: "0.85rem",
													lineHeight: "1.5",
												}}
											/>
										</div>
										<div className="col-12 d-flex gap-2">
											<button
												type="submit"
												className="btn btn-sm btn-primary"
												disabled={loading}
											>
												{loading
													? "Saving..."
													: editingEntry
														? "Update"
														: "Save"}
											</button>
											<button
												type="button"
												className="btn btn-sm btn-secondary"
												onClick={() => {
													setShowForm(false);
													setEditingEntry(null);
												}}
												disabled={loading}
											>
												Cancel
											</button>
										</div>
									</div>
								</form>
							</div>
						)}

						{/* Entries List */}
						{selectedEntries.length === 0 && !showForm ? (
							<div className="text-center py-4">
								<Dumbbell
									size={32}
									className="mb-2"
									style={{ color: "rgba(255,255,255,0.3)" }}
								/>
								<p className="text-muted mb-0" style={{ fontSize: "0.9rem" }}>
									No workouts logged for this day.
								</p>
							</div>
						) : (
							<div>
								{selectedEntries.map((entry) => {
									const entryRecords = recordsByEntry[entry.id] || [];
									const isRecordFormOpen = showRecordForm[entry.id];
									const isRecordsExpanded = expandedRecords[entry.id];
									const form = recordFormData[entry.id] || {
										movement: "",
										measurements: [],
									};
									const selectedTypes =
										form.measurements
											?.map((m) => m.countType)
											.filter(Boolean) || [];

									return (
										<div
											key={entry.id}
											className="mb-3 rounded"
											style={{
												backgroundColor: "#1e1e1e",
												border: "1px solid #333",
												overflow: "hidden",
											}}
										>
											{/* Entry header */}
											<div className="d-flex align-items-start gap-2 p-2">
												<div
													style={{
														width: "4px",
														alignSelf: "stretch",
														borderRadius: "2px",
														backgroundColor:
															typeColors[entry.type] || "#d15a36",
													}}
												/>
												<div className="flex-grow-1" style={{ minWidth: 0 }}>
													<div className="d-flex justify-content-between align-items-start">
														<div>
															<span
																style={{
																	color: "#fff",
																	fontWeight: 600,
																	fontSize: "0.9rem",
																}}
															>
																{entry.title}
															</span>
															<span
																className="badge ms-2"
																style={{
																	backgroundColor:
																		typeColors[entry.type] || "#d15a36",
																	fontSize: "0.65rem",
																}}
															>
																{entry.type}
															</span>
														</div>
														<div className="d-flex gap-1 flex-shrink-0">
															<button
																className="btn btn-sm p-1"
																onClick={() => openEditForm(entry)}
																style={{ color: "rgba(255,255,255,0.5)" }}
																title="Edit"
															>
																<Pencil size={14} />
															</button>
															<button
																className="btn btn-sm p-1"
																onClick={() => handleDelete(entry)}
																style={{ color: "rgba(255,255,255,0.5)" }}
																title="Delete"
															>
																<Trash2 size={14} />
															</button>
														</div>
													</div>
													{entry.description && (
														<p
															className="mb-0 mt-1"
															style={{
																color: "rgba(255,255,255,0.6)",
																fontSize: "0.8rem",
																whiteSpace: "pre-wrap",
															}}
														>
															{entry.description}
														</p>
													)}
													{/* Inline comments section */}
													<div className="mt-2">
														{editingComment[entry.id] ? (
															<div
																className="p-2 rounded"
																style={{
																	backgroundColor: "rgba(255,255,255,0.04)",
																	border: "1px solid rgba(255,255,255,0.08)",
																}}
															>
																<div className="d-flex align-items-center gap-1 mb-1">
																	<MessageSquare
																		size={12}
																		style={{ color: "#d15a36" }}
																	/>
																	<small
																		style={{
																			color: "#d15a36",
																			fontWeight: 600,
																			fontSize: "0.7rem",
																		}}
																	>
																		Comments
																	</small>
																</div>
																<textarea
																	className="form-control form-control-sm mb-2"
																	rows="3"
																	placeholder="Add your notes, thoughts, or modifications..."
																	value={commentDrafts[entry.id] ?? ""}
																	onChange={(e) =>
																		setCommentDrafts((prev) => ({
																			...prev,
																			[entry.id]: e.target.value,
																		}))
																	}
																	disabled={loading}
																	style={{
																		backgroundColor: "#1a1a1a",
																		color: "#fff",
																		border: "1px solid #555",
																		fontSize: "0.8rem",
																	}}
																	autoFocus
																/>
																<div className="d-flex gap-2">
																	<button
																		className="btn btn-sm btn-primary py-0 px-2"
																		style={{ fontSize: "0.75rem" }}
																		disabled={loading}
																		onClick={async () => {
																			try {
																				await onUpdateEntry(entry.id, {
																					comments: (
																						commentDrafts[entry.id] || ""
																					).trim(),
																				});
																				setEditingComment((prev) => ({
																					...prev,
																					[entry.id]: false,
																				}));
																			} catch (error) {
																				// error handled by context
																			}
																		}}
																	>
																		{loading ? "Saving..." : "Save"}
																	</button>
																	<button
																		className="btn btn-sm btn-secondary py-0 px-2"
																		style={{ fontSize: "0.75rem" }}
																		onClick={() =>
																			setEditingComment((prev) => ({
																				...prev,
																				[entry.id]: false,
																			}))
																		}
																		disabled={loading}
																	>
																		Cancel
																	</button>
																</div>
															</div>
														) : entry.comments ? (
															<div
																className="p-2 rounded"
																style={{
																	backgroundColor: "rgba(255,255,255,0.04)",
																	border: "1px solid rgba(255,255,255,0.08)",
																	cursor: "pointer",
																}}
																onClick={() => {
																	setCommentDrafts((prev) => ({
																		...prev,
																		[entry.id]: entry.comments,
																	}));
																	setEditingComment((prev) => ({
																		...prev,
																		[entry.id]: true,
																	}));
																}}
																title="Click to edit comment"
															>
																<div className="d-flex align-items-center gap-1 mb-1">
																	<MessageSquare
																		size={12}
																		style={{ color: "#d15a36" }}
																	/>
																	<small
																		style={{
																			color: "#d15a36",
																			fontWeight: 600,
																			fontSize: "0.7rem",
																		}}
																	>
																		Comments
																	</small>
																	<Pencil
																		size={10}
																		className="ms-1"
																		style={{ color: "rgba(255,255,255,0.3)" }}
																	/>
																</div>
																<p
																	className="mb-0"
																	style={{
																		color: "rgba(255,255,255,0.5)",
																		fontSize: "0.8rem",
																		whiteSpace: "pre-wrap",
																		fontStyle: "italic",
																	}}
																>
																	{entry.comments}
																</p>
															</div>
														) : (
															<button
																className="btn btn-sm py-0 px-2"
																onClick={() => {
																	setCommentDrafts((prev) => ({
																		...prev,
																		[entry.id]: "",
																	}));
																	setEditingComment((prev) => ({
																		...prev,
																		[entry.id]: true,
																	}));
																}}
																style={{
																	backgroundColor: "rgba(255,255,255,0.05)",
																	color: "rgba(255,255,255,0.4)",
																	border: "1px solid rgba(255,255,255,0.08)",
																	fontSize: "0.75rem",
																}}
															>
																<MessageSquare size={12} className="me-1" />
																Add Comment
															</button>
														)}
													</div>
												</div>
											</div>

											{/* Movement records section */}
											<div className="px-3 pb-2">
												<div className="d-flex align-items-center gap-2 mt-1">
													<button
														className="btn btn-sm py-0 px-2"
														onClick={() => toggleRecordForm(entry.id)}
														disabled={loading}
														style={{
															backgroundColor: "rgba(209,90,54,0.15)",
															color: "#d15a36",
															border: "1px solid rgba(209,90,54,0.3)",
															fontSize: "0.75rem",
														}}
													>
														<TrendingUp size={12} className="me-1" />
														Log Movement
													</button>
													{entryRecords.length > 0 && (
														<button
															className="btn btn-sm py-0 px-2"
															onClick={() =>
																setExpandedRecords((prev) => ({
																	...prev,
																	[entry.id]: !prev[entry.id],
																}))
															}
															style={{
																backgroundColor: "rgba(255,255,255,0.05)",
																color: "rgba(255,255,255,0.6)",
																border: "1px solid rgba(255,255,255,0.1)",
																fontSize: "0.75rem",
															}}
														>
															<Dumbbell size={12} className="me-1" />
															{entryRecords.length} movement
															{entryRecords.length !== 1 ? "s" : ""}
															{isRecordsExpanded ? (
																<ChevronUp size={12} className="ms-1" />
															) : (
																<ChevronDown size={12} className="ms-1" />
															)}
														</button>
													)}
												</div>

												{/* Inline record form */}
												{isRecordFormOpen && (
													<div
														className="mt-2 p-2 rounded"
														style={{
															backgroundColor: "#2a2a2a",
															border: "1px solid #444",
														}}
													>
														<div className="row g-2">
															<div className="col-12">
																<select
																	className="form-select form-select-sm"
																	value={form.movement}
																	onChange={(e) =>
																		updateRecordField(
																			entry.id,
																			"movement",
																			e.target.value,
																		)
																	}
																	disabled={loading}
																	style={darkInput}
																>
																	<option value="">Select movement</option>
																	{[...movements]
																		.sort((a, b) => a.localeCompare(b))
																		.map((m) => (
																			<option key={m} value={m}>
																				{m}
																			</option>
																		))}
																</select>
															</div>
															{form.measurements?.map((m, idx) => {
																const placeholder =
																	PLACEHOLDER_MAP[m.countType] || "Value";
																return (
																	<div key={idx} className="col-12">
																		<div className="d-flex gap-2 align-items-end">
																			<div style={{ flex: 1 }}>
																				<select
																					className="form-select form-select-sm"
																					value={m.countType}
																					onChange={(e) =>
																						updateRecordMeasurement(
																							entry.id,
																							idx,
																							"countType",
																							e.target.value,
																						)
																					}
																					disabled={loading}
																					style={darkInput}
																				>
																					<option value="">Type</option>
																					{countTypes.map((ct) => (
																						<option
																							key={ct.value}
																							value={ct.value}
																							disabled={
																								selectedTypes.includes(
																									ct.value,
																								) && ct.value !== m.countType
																							}
																						>
																							{ct.label} ({ct.unit})
																						</option>
																					))}
																				</select>
																			</div>
																			<div style={{ flex: 1 }}>
																				<input
																					type="text"
																					className="form-control form-control-sm"
																					value={m.value}
																					onChange={(e) =>
																						updateRecordMeasurement(
																							entry.id,
																							idx,
																							"value",
																							e.target.value,
																						)
																					}
																					placeholder={placeholder}
																					disabled={loading}
																					style={darkInput}
																				/>
																			</div>
																			{form.measurements.length > 1 && (
																				<button
																					type="button"
																					className="btn btn-outline-danger btn-sm"
																					onClick={() =>
																						removeRecordMeasurement(
																							entry.id,
																							idx,
																						)
																					}
																				>
																					<Trash2 size={12} />
																				</button>
																			)}
																		</div>
																	</div>
																);
															})}
															{(form.measurements?.length || 0) <
																countTypes.length && (
																<div className="col-12">
																	<button
																		type="button"
																		className="btn btn-outline-secondary btn-sm"
																		onClick={() =>
																			addRecordMeasurement(entry.id)
																		}
																		style={{
																			fontSize: "0.75rem",
																			color: "rgba(255,255,255,0.5)",
																			borderColor: "#444",
																		}}
																	>
																		<Plus size={12} className="me-1" />
																		Add Type
																	</button>
																</div>
															)}
															<div className="col-12 d-flex gap-2">
																<button
																	type="button"
																	className="btn btn-sm btn-primary"
																	onClick={() => handleAddRecord(entry.id)}
																	disabled={loading}
																>
																	{loading ? "Saving..." : "Save Movement"}
																</button>
																<button
																	type="button"
																	className="btn btn-sm btn-secondary"
																	onClick={() =>
																		setShowRecordForm((prev) => ({
																			...prev,
																			[entry.id]: false,
																		}))
																	}
																	disabled={loading}
																>
																	Cancel
																</button>
															</div>
														</div>
													</div>
												)}

												{/* Existing records list */}
												{isRecordsExpanded && entryRecords.length > 0 && (
													<div className="mt-2">
														{entryRecords.map((rec) => {
															const meas =
																rec.measurements && rec.measurements.length > 0
																	? rec.measurements
																	: [
																			{
																				countTypeLabel: rec.countTypeLabel,
																				countUnit: rec.countUnit,
																				value: rec.value,
																			},
																		];
															return (
																<div
																	key={rec.id}
																	className="d-flex justify-content-between align-items-center py-1 px-2 mb-1 rounded"
																	style={{
																		backgroundColor: "rgba(255,255,255,0.03)",
																		border: "1px solid rgba(255,255,255,0.06)",
																	}}
																>
																	<div>
																		<span
																			style={{
																				color: "#d15a36",
																				fontWeight: 600,
																				fontSize: "0.8rem",
																			}}
																		>
																			{rec.movement}
																		</span>
																		<span
																			className="ms-2"
																			style={{
																				color: "rgba(255,255,255,0.6)",
																				fontSize: "0.8rem",
																			}}
																		>
																			{meas
																				.map((m) => `${m.value} ${m.countUnit}`)
																				.join(" × ")}
																		</span>
																	</div>
																	<button
																		className="btn btn-sm p-0"
																		onClick={() => handleDeleteRecord(rec.id)}
																		style={{ color: "rgba(255,255,255,0.3)" }}
																		title="Delete"
																	>
																		<Trash2 size={12} />
																	</button>
																</div>
															);
														})}
													</div>
												)}
											</div>
										</div>
									);
								})}
							</div>
						)}
					</div>
				)}
			</div>

			<CustomModal
				show={modal.show}
				title={modal.title}
				message={modal.message}
				type={modal.type}
				onClose={() => setModal({ ...modal, show: false })}
			/>
			<CustomModal
				show={confirmModal.show}
				title={confirmModal.title}
				message={confirmModal.message}
				type={confirmModal.type}
				onConfirm={confirmModal.onConfirm}
				onClose={() => setConfirmModal({ ...confirmModal, show: false })}
				confirmText="Delete"
				loading={loading}
			/>
		</div>
	);
};

export default TrainerPlanner;
